import { useEffect, useMemo, useState } from "react";
import { skipToken } from "@reduxjs/toolkit/query";

import { useGetCertificationProgressQuery } from "@/redux/service";
import type {
  CertificationDetail,
  CertificationProgress,
} from "@/redux/certifications/types";
import {
  buildModuleOverviews,
  isProgressSettled,
} from "@/domains/certifications/progress";
import { ENROLLMENT_STATES } from "@/domains/certifications/constants";

const POLL_INTERVAL_MS = 2000;
const MAX_POLLS = 10;

export const useCertificationProgress = (
  certification: CertificationDetail | undefined,
) => {
  const isEnrolled =
    certification?.enrollment?.state === ENROLLMENT_STATES.ACTIVE;
  const [polls, setPolls] = useState(0);
  const [isSettled, setIsSettled] = useState(true);
  const [lastSettled, setLastSettled] = useState<CertificationProgress>();

  const query = useGetCertificationProgressQuery(
    certification && isEnrolled ? certification.slug : skipToken,
    { pollingInterval: !isSettled && polls < MAX_POLLS ? POLL_INTERVAL_MS : 0 },
  );
  const { data: progress, fulfilledTimeStamp } = query;

  const settled = useMemo(
    () =>
      !certification ||
      !progress ||
      isProgressSettled(
        certification.status,
        buildModuleOverviews(certification, progress, progress.selectedTrack),
      ),
    [certification, progress],
  );

  useEffect(() => {
    setIsSettled(settled);
    setPolls((count) => (settled ? 0 : count + 1));
    if (settled && progress) {
      setLastSettled(progress);
    }
  }, [settled, progress, fulfilledTimeStamp]);

  const isSettling = !settled && polls < MAX_POLLS;

  return { ...query, data: isSettling ? (lastSettled ?? progress) : progress };
};
